import { memoize, uniqueId } from "nodash"
import {
    RunTaskOptions,
    WorkerInbound,
    WorkerOutbound,
    wasmBinaries,
    WasmBinaryName,
} from "../shared"
import type { AnyTask, TaskArgs, TaskResult } from "./task"

export type PoolWorker = {
    postMessage: (message: WorkerInbound, transfer: ArrayBuffer[]) => void
    onMessage: (listener: (message: WorkerOutbound) => void) => () => void
    terminate: () => void
}

export type PoolAdapter = {
    createWorker: () => PoolWorker
    loadWasmBinary: (fileName: string) => Promise<BufferSource>
}

export const createPool = (adapter: PoolAdapter, workerCount: number) => {
    const slots = Array.from({ length: workerCount }, () => ({
        worker: adapter.createWorker(),
        tasksInProgress: 0,
    }))

    const compileWasm = memoize((name: WasmBinaryName) =>
        adapter.loadWasmBinary(wasmBinaries[name]).then((bytes) => WebAssembly.compile(bytes))
    )

    const runTask = async <T extends AnyTask>(
        task: T,
        args: TaskArgs<T>,
        { signal, transfer = [] }: RunTaskOptions = {}
    ) => {
        const wasm = await Promise.all(
            task.wasm.map(async (name) => ({ name, module: await compileWasm(name) }))
        )
        const slot = slots.reduce((acc, cur) =>
            acc.tasksInProgress < cur.tasksInProgress ? cur : acc
        )
        const taskId = uniqueId("task")
        slot.tasksInProgress++
        try {
            const data = await new Promise<unknown>((resolve, reject) => {
                const abort = () => slot.worker.postMessage({ type: "abort", taskId }, [])
                const stopListening = slot.worker.onMessage((message) => {
                    if (message.taskId !== taskId) return
                    stopListening()
                    signal?.removeEventListener("abort", abort)
                    if (message.type === "done") resolve(message.data)
                    else reject(message.error)
                })
                slot.worker.postMessage(
                    { type: "run", taskId, taskName: task.name, args, wasm },
                    transfer
                )
                if (signal?.aborted) abort()
                else signal?.addEventListener("abort", abort)
            })
            return data as TaskResult<T>
        } finally {
            slot.tasksInProgress--
        }
    }

    const terminate = () => {
        for (const { worker } of slots) worker.terminate()
    }

    return { runTask, terminate }
}
